/**
 * A re-import on the page (decision 64; part 23b, table V′): a Word file read in part 17's sandbox by part 23a's reader,
 * its pictures decided (table PI), its chapters matched and merged into the open book's in a DOCX worker (tables MA, MG),
 * the outcome shown for review, and only on the writer's word the new pictures added (part 16a) and everything written
 * at once (table AP). Cancelling, closing the review or any refusal writes nothing.
 *
 * The sandbox, the book's reads, the workers and the workspace's write are injected, so the logic runs in tests.
 */
import { imageDestination, settleImages } from '@cockatrice/core';
import type { ImageReference, MergedChapter } from '@cockatrice/core';
import { BOOK_LANGUAGES } from '@cockatrice/layout';
import type { BookLanguage } from '@cockatrice/layout';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { MergeChapter, MergeJob } from '../docx/protocol.ts';
import type { ImportResult } from '../images/importer.ts';
import type { ImportOutcome, ImportProblem, ImportRequest } from '../import/host.ts';
import { importKind } from '../import/kind.ts';
import type { ReimportedReply } from '../import/protocol.ts';
import type { ExportedBook } from '../shell/book-export.ts';
import { cleanComment, identifyComments } from './comments.ts';
import type { IdentifiedComment } from './comments.ts';
import { decidePicture, settlement } from './pictures.ts';
import type { DecidedPicture, PictureLookup } from './pictures.ts';
import type { ExportBase, ReimportCommitResult, ReimportRequest, StoredPicture } from './reimport.types.ts';
import { mergeJob, planReimport, reviewOf, writeOf } from './review.ts';
import type { Plan, Review } from './review.ts';

/**
 * Why a re-import ended with nothing written: the sandbox's or reader's problem (V′4), not a Word file (V′1), no book
 * open or one that can't be read whole, the merge failing (V′4), or the write's (V′7, CS11, CS12).
 */
export type ReimportFailure = ImportProblem | 'not-supported' | 'unreadable' | 'merge-failed' | 'book-changed' | 'not-editable' | 'save-failed' | 'changed' | 'stale' | 'write-failed';

/** What the re-import shows (table V′'s outcomes). */
export type ReimportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'reading'; readonly fileName: string; readonly done: number; readonly total: number }
  | { readonly kind: 'comparing'; readonly done: number; readonly total: number }
  | { readonly kind: 'merging'; readonly done: number; readonly total: number }
  | { readonly kind: 'review'; readonly fileName: string; readonly review: Review; readonly pictures: number }
  | { readonly kind: 'adding'; readonly done: number; readonly total: number }
  | { readonly kind: 'storing' }
  | { readonly kind: 'done'; readonly changed: number; readonly added: number; readonly copies: number; readonly missing: number; readonly comments: number }
  | { readonly kind: 'failed'; readonly reason: ReimportFailure };

/** What the controller needs; everything with an effect is injected. */
export interface ReimportOptions {
  /** Part 17's sandbox, reading `file` as a re-import (`ImportSandbox.run` with `reimport`). */
  readonly run: (file: Blob, request: ImportRequest) => Promise<ImportOutcome>;
  /** The open book's ID, or null with none (`Workspace.book`). */
  readonly openBook: () => string | null;
  /** The open book as the re-import reads it (`Workspace.reimportBook`). */
  readonly book: () => Promise<ExportedBook | null>;
  /** A chapter's export base in a book (`Workspace.exportBase`). */
  readonly base: (bookId: string, chapterId: string, snapshotId: string) => Promise<ExportBase | null>;
  /** Table PI's lookups, each in the book named (`Workspace.storedImage`, `imageByHash`), and the hashing and comparing. */
  readonly pictures: {
    readonly stored: (bookId: string, id: string) => Promise<StoredPicture | null>;
    readonly byHash: (bookId: string, sha256: string) => Promise<ImageReference | null>;
  } & Pick<PictureLookup, 'digest' | 'same'>;
  /** The merge in a DOCX worker (`runMerge`): one merged chapter per chapter asked, or null. */
  readonly merge: (job: MergeJob, onProgress: (done: number, total: number) => void, signal: AbortSignal) => Promise<readonly MergedChapter[] | null>;
  /** Part 16a's importer for one picture into book `bookId` (`importImage`, purpose `text`). */
  readonly addPicture: (bookId: string, file: Blob) => Promise<ImportResult>;
  /** The workspace's write (`Workspace.reimport`). */
  readonly commit: (request: ReimportRequest) => Promise<ReimportCommitResult>;
}

/**
 * KY2 (deep-fix r2): the language the reference is read back in: the one the file names (part 22 writes the book's
 * language into its style defaults), so its quotes are set as the file's were, whatever the book's language is now; the
 * book's when the file names none this app knows.
 */
function exportedIn(named: string | null, now: BookLanguage): BookLanguage {
  return BOOK_LANGUAGES.find((language) => language === named) ?? now;
}

/** Every image destination a text names (part 16a's shape), each a candidate the book may hold. */
const DESTINATIONS = /image:[0-9a-f]{32}\?sha256=[0-9a-f]{64}/gu;

/** What a review stands on, kept for its Apply. */
interface Reviewed {
  readonly bookId: string;
  readonly plan: Plan;
  readonly merged: readonly MergedChapter[];
  readonly pictures: readonly DecidedPicture[];
  /** Part 23c: the file's comments placed in a chapter, cleaned and with their IDs (table ID). */
  readonly comments: readonly IdentifiedComment[];
}

/** The re-import's state and its one run. */
export class ReimportController {
  readonly state: Accessor<ReimportState>;
  readonly #setState: (state: ReimportState) => void;
  readonly #options: ReimportOptions;
  /** The re-import under way: a new number for each, so what an earlier one gives is ignored. */
  #job = 0;
  #abort: AbortController | null = null;
  #reviewed: Reviewed | null = null;

  constructor(options: ReimportOptions) {
    this.#options = options;
    const [state, setState] = createSignal<ReimportState>({ kind: 'idle' });
    this.state = state;
    this.#setState = setState;
  }

  /** Whether a re-import is reading, comparing, merging, adding pictures or storing (V′2: the button is off then). */
  busy(): boolean {
    const kind = this.state().kind;
    return kind === 'reading' || kind === 'comparing' || kind === 'merging' || kind === 'adding' || kind === 'storing';
  }

  /** V′1–V′5: reads `file` and merges it into the open book; its review follows, or why there is none. */
  pick(file: File): void {
    if (this.busy()) return;
    this.#job += 1;
    const job = this.#job;
    this.#reviewed = null;
    // V′1: refused before a byte is read.
    if (importKind(file.name) !== 'docx') {
      this.#setState({ kind: 'failed', reason: 'not-supported' });
      return;
    }
    const abort = new AbortController();
    this.#abort = abort;
    this.#setState({ kind: 'reading', fileName: file.name, done: 0, total: file.size });
    void this.#read(job, file, abort.signal).catch(() => {
      this.#fail(job, 'failed');
    });
  }

  /** V′3: stops the reading, comparing, merging or adding of pictures; nothing is written. */
  cancel(): void {
    const kind = this.state().kind;
    if (kind !== 'reading' && kind !== 'comparing' && kind !== 'merging' && kind !== 'adding') return;
    this.#abort?.abort();
    this.#abort = null;
    this.#job += 1;
    this.#reviewed = null;
    this.#setState({ kind: 'idle' });
  }

  /** V′6, and putting a failure or an outcome away: back to nothing, the review's data dropped. */
  dismiss(): void {
    if (this.busy()) return;
    this.#job += 1;
    this.#reviewed = null;
    this.#setState({ kind: 'idle' });
  }

  /** V′6: the review put away without applying (Cancel, Escape, the dialog closed); nothing is written. */
  closeReview(): void {
    if (this.state().kind !== 'review') return;
    this.#job += 1;
    this.#reviewed = null;
    this.#setState({ kind: 'idle' });
  }

  /**
   * V′8: the reviewed re-import written, chapter `i` of the file taken when `take[i]` (CS14) and its file title when
   * `titles[i]` (CS8); settles when it ends.
   */
  confirm(take: readonly boolean[], titles: readonly boolean[] = []): Promise<void> {
    const reviewed = this.#reviewed;
    if (this.state().kind !== 'review' || reviewed === null) return Promise.resolve();
    this.#job += 1;
    const abort = new AbortController();
    this.#abort = abort;
    return this.#store(this.#job, reviewed, take, titles, abort.signal);
  }

  /** V′3–V′5: the file read, its pictures decided, its chapters planned and merged, then the review. */
  async #read(job: number, file: File, signal: AbortSignal): Promise<void> {
    const outcome = await this.#options.run(file, {
      signal,
      reimport: true,
      onProgress: (done, total) => {
        if (job === this.#job) this.#setState({ kind: 'reading', fileName: file.name, done, total });
      },
    });
    if (job !== this.#job) return;
    if (outcome.kind !== 'reimported') {
      // V′3: a cancel ends in nothing to show; a reply of another kind is no re-import.
      if (outcome.kind === 'refused' && outcome.problem === 'cancelled') this.#setState({ kind: 'idle' });
      else this.#fail(job, outcome.kind === 'refused' ? outcome.problem : 'failed');
      return;
    }
    const book = await this.#options.book();
    if (job !== this.#job) return;
    if (book === null) {
      this.#fail(job, 'unreadable');
      return;
    }
    // V′9 (deep-fix r1): every lookup is in the book read here; another opened meanwhile ends the re-import.
    const bookId = book.book.id;
    if (!this.#on(job, bookId)) return;
    const lookup = this.#lookup(bookId);
    const pictures = await this.#decide(job, outcome, lookup);
    if (pictures === null || !this.#on(job, bookId)) return;
    const settled = settlement(pictures);
    const bases = await this.#bases(bookId, planReimport(outcome.chapters, book, new Map(), settled));
    if (job !== this.#job || !this.#on(job, bookId)) return;
    const plan = planReimport(outcome.chapters, book, bases, settled);
    const chapters = mergeJob(plan);
    const held = await this.#held(chapters, lookup);
    if (job !== this.#job || !this.#on(job, bookId)) return;
    this.#setState({ kind: 'merging', done: 0, total: chapters.length });
    const merged = await this.#options.merge(
      { kind: 'merge', book: { ...book.book, metadata: { ...book.book.metadata, language: exportedIn(outcome.language, book.book.metadata.language) }, chapters: [] }, held, chapters },
      (done, total) => {
        if (job === this.#job) this.#setState({ kind: 'merging', done, total });
      },
      signal,
    );
    if (job !== this.#job) return;
    if (merged === null) {
      this.#fail(job, 'merge-failed');
      return;
    }
    // Part 23c, table ID″: each comment cleaned and given its key.
    const comments = await identifyComments(outcome.comments.map(cleanComment), this.#options.pictures.digest);
    if (job !== this.#job || !this.#on(job, bookId)) return;
    this.#abort = null;
    this.#reviewed = { bookId, plan, merged, pictures, comments };
    const review = reviewOf(plan, merged, outcome.flags, outcome.comments);
    this.#setState({ kind: 'review', fileName: file.name, review, pictures: pictures.filter(({ decision }) => decision.kind === 'new').length });
  }

  /** V′9 (deep-fix r1): whether `bookId` is still the open book; if not, the re-import `job` ends, nothing written. */
  #on(job: number, bookId: string): boolean {
    if (this.#options.openBook() === bookId) return true;
    this.#fail(job, 'book-changed');
    return false;
  }

  /** Table PI's lookups in book `bookId`. */
  #lookup(bookId: string): PictureLookup {
    const { stored, byHash, digest, same } = this.#options.pictures;
    return { stored: (id) => stored(bookId, id), byHash: (sha256) => byHash(bookId, sha256), digest, same };
  }

  /** Table PI for each picture of `file`, the state saying how far it has come; null once the re-import is no longer `job`. */
  async #decide(job: number, file: ReimportedReply, lookup: PictureLookup): Promise<DecidedPicture[] | null> {
    const decided: DecidedPicture[] = [];
    for (const picture of file.pictures) {
      this.#setState({ kind: 'comparing', done: decided.length, total: file.pictures.length });
      const decision = await decidePicture(picture, lookup);
      if (job !== this.#job) return null;
      decided.push({ picture, decision });
    }
    return decided;
  }

  /**
   * KY4: the image destinations of `chapters`' references the book holds, as the export finds them (each one's image
   * stored under its ID with that hash), so the reference's reading writes them as the file does.
   */
  async #held(chapters: readonly MergeChapter[], lookup: PictureLookup): Promise<string[]> {
    const named = new Set(chapters.flatMap((chapter) => [chapter.base ?? '', chapter.current].flatMap((text) => text.match(DESTINATIONS) ?? [])));
    const held: string[] = [];
    for (const destination of named) {
      // `image:` then the ID's 32 digits.
      const stored = await lookup.stored(destination.slice(6, 38));
      if (stored !== null && imageDestination(stored.reference) === destination) held.push(destination);
    }
    return held;
  }

  /** MA8, CS2–CS5: each matched chapter's export base, from the snapshot its file chapter names, by chapter ID. */
  async #bases(bookId: string, plan: Plan): Promise<Map<string, ExportBase>> {
    const bases = new Map<string, ExportBase>();
    for (const { file, match } of plan.chapters) {
      if (match.kind !== 'matched' || file.snapshot === null) continue;
      const base = await this.#options.base(bookId, match.id, file.snapshot);
      if (base !== null) bases.set(match.id, base);
    }
    return bases;
  }

  /** V′8: the new pictures the taken texts name added one at a time (PI10: one not added keeps its placeholder), then the write. */
  async #store(job: number, reviewed: Reviewed, take: readonly boolean[], titles: readonly boolean[], signal: AbortSignal): Promise<void> {
    // V′9 (deep-fix r1): nothing added or written into a book other than the one reviewed.
    if (!this.#on(job, reviewed.bookId)) return;
    const write = writeOf(reviewed.bookId, reviewed.plan, reviewed.merged, take, reviewed.pictures, titles);
    const added = new Map<string, string>();
    let missing = 0;
    for (const [i, { key, file }] of write.pictures.entries()) {
      this.#setState({ kind: 'adding', done: i, total: write.pictures.length });
      const result = await this.#options.addPicture(reviewed.bookId, file).catch((): ImportResult => ({ kind: 'failed' }));
      if (job !== this.#job || signal.aborted) return;
      if (result.kind === 'added' || result.kind === 'existing') added.set(key, imageDestination(result.reference));
      else missing += 1;
    }
    this.#abort = null;
    this.#setState({ kind: 'storing' });
    // Part 23c, table AN: each comment anchored as the write leaves its chapter, the page's own work (bounded by AN8 and
    // `anchor.bench.ts`), after a turn so the status shows first.
    await new Promise((resolve) => setTimeout(resolve, 0));
    const anchored = writeOf(reviewed.bookId, reviewed.plan, reviewed.merged, take, reviewed.pictures, titles, reviewed.comments).request;
    const request: ReimportRequest = {
      bookId: anchored.bookId,
      taken: anchored.taken.map((chapter) => ({ ...chapter, after: settleImages(chapter.after, added) })),
      added: anchored.added.map((chapter) => ({ ...chapter, text: settleImages(chapter.text, added) })),
      commented: anchored.commented,
    };
    const result = await this.#options.commit(request).catch((): ReimportCommitResult => ({ kind: 'failed' }));
    this.#reviewed = null;
    if (result.kind === 'reimported') this.#setState({ kind: 'done', changed: result.changed, added: result.added, copies: result.copies, missing, comments: result.comments });
    else this.#setState({ kind: 'failed', reason: result.kind === 'failed' ? 'write-failed' : result.kind });
  }

  /** V′4: the re-import `job` ends with nothing written, saying why. */
  #fail(job: number, reason: ReimportFailure): void {
    if (job !== this.#job) return;
    this.#abort = null;
    this.#reviewed = null;
    this.#setState({ kind: 'failed', reason });
  }
}
